import ExcelJS from 'exceljs'
import type { BlankSheetRow } from './BlankDevanSheet'

// Z50.X.4/.5: Excel export rebuilt to match BlankDevanSheetPDF's actual layout —
// same title row (job/container title, warehouse code, page number), same column
// set/order, same TOTAL row, same footer — rather than the earlier
// warehouse-metadata-block design. The only intentional additions beyond the PDF
// are: writable blank cells (P1-P15, Act Ctns, etc. are read-only text in the PDF,
// editable cells here) and a Notes/Remarks column on the Full sheet only.
//
// P1-P15: the PDF embeds a 5x3 sub-grid *inside every data row's single cell*.
// A flat spreadsheet can't nest a grid inside one cell, so this uses 15 real
// sub-columns instead, with "P1".."P15" labelled once in the header (not
// repeated per row like the PDF) — full pallet-editing without 15x the row count.
//
// Title row: a real worksheet row 1 (visible immediately in Normal view — an
// earlier version put this only in Excel's native print header, which is
// invisible outside Print Preview/Page Layout view, and was reported "missing").
// It does NOT repeat via printTitlesRow — only the table's own column-header
// row(s) do — because a plain cell can't know the true page count the way
// Excel's native &P/&N header codes can, so a repeated static "Page 1 of N"
// would be wrong on every page after the first. Row 1's own page indicator is
// literal "Page 1" (always true, since it only ever appears once); the native
// header (still set) additionally shows an accurate "Page X of Y" when printed.
//
// Uses ExcelJS (not the `xlsx` package) — see Plans/Detail.tsx handleDownloadExcel
// for why: `xlsx`'s community edition silently drops cell styles on write.

export interface DevanSheetExcelOptions {
  jobRef:         string
  containerNo:    string
  warehouseCode:  string
  plannedDevanAt: string | null
  rows:           BlankSheetRow[]
  type:           'full' | 'simple'
}

const BORDER_COLOR = 'FF000000' // "thin black lines" per spec — PDF's own borders are
                                 // a lighter slate, but this ticket asks for black
const THIN = { style: 'thin' as const, color: { argb: BORDER_COLOR } }
const MEDIUM = { style: 'medium' as const, color: { argb: BORDER_COLOR } }
const ALL_BORDERS = { top: THIN, left: THIN, bottom: THIN, right: THIN }
const HEADER_FILL = 'FFE2E8F0' // light grey
const HOLD_COLOR  = 'FFB45309' // matches BlankDevanSheetPDF's holdTxt amber

const PALLET_COUNT = 15
const FULL_ROW_MIN_HEIGHT   = 70  // pt — per spec, room for handwritten pallet counts
const SIMPLE_ROW_MIN_HEIGHT = 30

function fmtDate(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

// Rough wrapped-line estimate: Excel's column "width" is defined as roughly the
// number of default-font characters that fit, so treat it as chars-per-line directly.
function estimateLines(text: string, colWidth: number): number {
  if (!text) return 1
  const charsPerLine = Math.max(4, Math.floor(colWidth))
  return text.split('\n').reduce((sum, line) => sum + Math.max(1, Math.ceil(line.length / charsPerLine)), 0)
}

function setPageSetup(ws: ExcelJS.Worksheet) {
  ws.pageSetup.paperSize    = 9 // A4
  ws.pageSetup.orientation  = 'landscape'
  ws.pageSetup.margins      = { left: 0.5, right: 0.5, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 }
  ws.pageSetup.fitToPage    = true
  ws.pageSetup.fitToWidth   = 1
  ws.pageSetup.fitToHeight  = 0
}

// Native Excel print header/footer — invisible in Normal view, but the only way
// to get a genuinely accurate "Page X of Y" (&P/&N are evaluated by Excel per
// printed page). Kept as a belt-and-suspenders on top of the visible row 1 title.
function setNativeHeader(ws: ExcelJS.Worksheet, titleLeft: string, warehouseCode: string, eta: string) {
  const right = (eta ? `ETA: ${eta}    ` : '') + 'Page &P of &N'
  ws.headerFooter.oddHeader  = `&L&B&11${titleLeft}&B&C&9${warehouseCode}&R&9${right}`
  ws.headerFooter.evenHeader = ws.headerFooter.oddHeader
}

// Visible title row (row 1): left third bold title, center third warehouse code,
// right third "Page 1" — this row is never repeated across printed pages (see
// module doc), so "Page 1" is always literally true.
function addTitleRow(ws: ExcelJS.Worksheet, titleLeft: string, warehouseCode: string, totalCols: number) {
  const row = ws.getRow(1)
  const leftEnd = Math.max(1, Math.round(totalCols / 3))
  const midEnd  = Math.max(leftEnd + 1, Math.round((totalCols * 2) / 3))
  ws.mergeCells(1, 1, 1, leftEnd)
  ws.mergeCells(1, leftEnd + 1, 1, midEnd)
  ws.mergeCells(1, midEnd + 1, 1, totalCols)

  const leftCell = row.getCell(1)
  leftCell.value = titleLeft
  leftCell.font = { bold: true, size: 12 }
  leftCell.alignment = { horizontal: 'left', vertical: 'middle' }

  const midCell = row.getCell(leftEnd + 1)
  midCell.value = warehouseCode
  midCell.font = { bold: true, size: 11 }
  midCell.alignment = { horizontal: 'center', vertical: 'middle' }

  const rightCell = row.getCell(midEnd + 1)
  rightCell.value = 'Page 1'
  rightCell.font = { size: 10 }
  rightCell.alignment = { horizontal: 'right', vertical: 'middle' }

  row.height = 24
  for (let c = 1; c <= totalCols; c++) row.getCell(c).border = { bottom: MEDIUM }
}

function styleHeaderCell(cell: ExcelJS.Cell) {
  cell.font = { bold: true }
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL } }
  cell.border = ALL_BORDERS
  // Header labels are short and columns are sized to fit them on one line —
  // wrapping was turning "T.PLTS"/"Act Sum"/"PLT Location" into 2-line headers.
  cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: false }
}

// Applies a full box border (all 4 sides, on every cell in the range — Excel
// draws each cell's own edges, so a merge only gets a complete box if every
// cell in it carries the matching border) and a top-aligned caption label.
function addFooterBox(ws: ExcelJS.Worksheet, rowNum: number, from: number, to: number, label: string) {
  ws.mergeCells(rowNum, from, rowNum, to)
  for (let col = from; col <= to; col++) {
    ws.getRow(rowNum).getCell(col).border = ALL_BORDERS
  }
  const cell = ws.getRow(rowNum).getCell(from)
  cell.value = label
  cell.font = { bold: true, size: 8 }
  cell.alignment = { horizontal: 'left', vertical: 'top' }
}

export async function generateDevanSheetExcelBlob(opts: DevanSheetExcelOptions): Promise<Blob> {
  return opts.type === 'full' ? buildFullSheet(opts) : buildSimpleSheet(opts)
}

// ── FULL ───────────────────────────────────────────────────────────────────────
// Mirrors BlankDevanSheetPDF's 'full' COLS exactly (17 columns), P1-P15 expanded
// into 15 editable sub-columns, plus one extra Notes/Remarks column for pen notes.
// Row layout: 1 = title, 2 = column super-header, 3 = P1..P15 sub-labels, 4+ = data.
async function buildFullSheet(opts: DevanSheetExcelOptions): Promise<Blob> {
  const { jobRef, containerNo, warehouseCode, plannedDevanAt, rows } = opts
  const titleLeft = [jobRef, containerNo].filter(Boolean).join(' · ') + ' — DEVAN SHEET (FULL)'
  const eta = fmtDate(plannedDevanAt)

  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet('Devan Sheet (Full)')
  setPageSetup(ws)
  setNativeHeader(ws, titleLeft, warehouseCode, eta)

  // col index -> { label, width, ...} for the non-pallet columns, in PDF order.
  // Widths bumped from the original spec numbers where those were too tight and
  // produced ##### on real data (Z50.X.5 bug B) or wrapped 1-line headers (bug C).
  //
  // Destination/Job Ref/Act Ctns/Act Sum/✓/Note removed from this Plan sheet
  // (redundant with Group No, or belong to the post-devanning actual record —
  // see buildActualSheet, a second tab in the same workbook) -- Destination is
  // just Group No's own grouping restated, Job Ref reconstructs from the sheet
  // title's job_ref + Split No (e.g. KI19024M + "-06"), and Act Ctns/Act Sum/✓
  // are actual-devanning data that doesn't exist yet on this pre-devanning sheet.
  const LEFT_COLS = [
    { id: 'splitNo',     label: 'Group No',     width: 8,  wrap: true },
    { id: 'splitNos',    label: 'Split No',     width: 6,  wrap: true },
    { id: 'sysRefs',     label: 'N/M',          width: 10, wrap: true },
    { id: 'delMode',     label: 'Del. Mode',    width: 10 },
    { id: 'qty',         label: 'Qty',          width: 7,  numFmt: '0',    align: 'center' as const },
    { id: 'wtKg',        label: 'WT(KG)',       width: 10, numFmt: '0.0' },
    { id: 'cbm',         label: 'CBM',          width: 8,  numFmt: '0.00' },
    { id: 'isHold',      label: 'Hold',         width: 6,  align: 'center' as const },
  ]
  const RIGHT_COLS = [
    { id: 'actTotalPallets', label: 'T.PLTS',      width: 7, align: 'center' as const },
    { id: 'pltLocation',     label: 'PLT Location', width: 14 },
    { id: 'notesRemarks',    label: 'Notes / Remarks', width: 30, wrap: true },
  ]
  const colIndexOf = (id: string) => LEFT_COLS.findIndex(c => c.id === id) + 1

  const palletStartCol = LEFT_COLS.length + 1
  const palletEndCol   = palletStartCol + PALLET_COUNT - 1
  const rightStartCol  = palletEndCol + 1
  const totalCols      = rightStartCol + RIGHT_COLS.length - 1

  ws.columns = [
    ...LEFT_COLS.map(c => ({ width: c.width })),
    ...Array.from({ length: PALLET_COUNT }, () => ({ width: 3.6 })),
    ...RIGHT_COLS.map(c => ({ width: c.width })),
  ]

  addTitleRow(ws, titleLeft, warehouseCode, totalCols)

  // ── 2-row table header: row 2 holds every column's label (vertically merged
  // into row 3 for non-pallet columns), row 3 carries the P1..P15 sub-labels. ──
  const HDR1 = 2, HDR2 = 3
  const hdrRow1 = ws.getRow(HDR1)
  const hdrRow2 = ws.getRow(HDR2)
  LEFT_COLS.forEach((c, i) => {
    const col = i + 1
    ws.mergeCells(HDR1, col, HDR2, col)
    hdrRow1.getCell(col).value = c.label
    styleHeaderCell(hdrRow1.getCell(col))
  })
  ws.mergeCells(HDR1, palletStartCol, HDR1, palletEndCol)
  hdrRow1.getCell(palletStartCol).value = 'P1-P15'
  styleHeaderCell(hdrRow1.getCell(palletStartCol))
  for (let p = 0; p < PALLET_COUNT; p++) {
    const cell = hdrRow2.getCell(palletStartCol + p)
    cell.value = `P${p + 1}`
    cell.font = { bold: true, size: 7 }
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL } }
    cell.border = ALL_BORDERS
    cell.alignment = { horizontal: 'center', vertical: 'middle' }
  }
  RIGHT_COLS.forEach((c, i) => {
    const col = rightStartCol + i
    ws.mergeCells(HDR1, col, HDR2, col)
    hdrRow1.getCell(col).value = c.label
    styleHeaderCell(hdrRow1.getCell(col))
  })
  hdrRow1.height = 16
  hdrRow2.height = 12

  // ── Data rows ──────────────────────────────────────────────────────────────
  rows.forEach(row => {
    const excelRow = ws.getRow(ws.rowCount + 1)
    LEFT_COLS.forEach((c, i) => {
      const col = i + 1
      const cell = excelRow.getCell(col)
      cell.value =
        c.id === 'isHold' ? (row.isHold ? '⚠ HOLD' : '') :
        c.id === 'qty'  ? row.qty :
        c.id === 'wtKg' ? (row.wtKg ?? null) :
        c.id === 'cbm'  ? (row.cbm  ?? null) :
        (row as unknown as Record<string, unknown>)[c.id] as string ?? ''
      cell.border = ALL_BORDERS
      cell.alignment = { vertical: 'top', wrapText: !!c.wrap, horizontal: c.align }
      if ('numFmt' in c && c.numFmt) cell.numFmt = c.numFmt
      if (c.id === 'isHold' && row.isHold) cell.font = { bold: true, color: { argb: HOLD_COLOR } }
    })
    for (let p = 0; p < PALLET_COUNT; p++) {
      const cell = excelRow.getCell(palletStartCol + p)
      cell.border = ALL_BORDERS // blank — warehouse writes actual carton counts by hand
    }
    RIGHT_COLS.forEach((c, i) => {
      const cell = excelRow.getCell(rightStartCol + i)
      cell.border = ALL_BORDERS
      cell.alignment = { vertical: 'top', wrapText: !!c.wrap }
      // T.PLTS / PLT Location / Notes-Remarks are all blank fill-in-by-hand cells
      // on this pre-devanning sheet, same as the PDF.
    })
    const maxLines = Math.max(1, estimateLines(row.sysRefs, LEFT_COLS[colIndexOf('sysRefs') - 1].width))
    excelRow.height = Math.max(FULL_ROW_MIN_HEIGHT, maxLines * 14 + 10)
  })

  // ── TOTAL row ──────────────────────────────────────────────────────────────
  const totalQty = rows.reduce((s, r) => s + r.qty, 0)
  const totalWt  = rows.reduce((s, r) => s + (r.wtKg ?? 0), 0)
  const totalCbm = rows.reduce((s, r) => s + (r.cbm  ?? 0), 0)
  const totalRow = ws.getRow(ws.rowCount + 1)
  for (let col = 1; col <= totalCols; col++) {
    const cell = totalRow.getCell(col)
    cell.border = ALL_BORDERS
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL } }
    cell.font = { bold: true }
  }
  totalRow.getCell(1).value = 'TOTAL'
  totalRow.getCell(colIndexOf('qty')).value = totalQty
  totalRow.getCell(colIndexOf('qty')).numFmt = '0'
  totalRow.getCell(colIndexOf('wtKg')).value = totalWt || ''
  totalRow.getCell(colIndexOf('wtKg')).numFmt = '0.0'
  totalRow.getCell(colIndexOf('cbm')).value = totalCbm || ''
  totalRow.getCell(colIndexOf('cbm')).numFmt = '0.00'

  // ── Footer: 3 bordered boxes — Devanned by / Date / Signature — matches the
  // PDF's 'full' footer (each box's own border doubles as the sign line). ──
  const footRowNum = ws.rowCount + 1
  ws.getRow(footRowNum).height = 40
  const third = Math.max(1, Math.floor(totalCols / 3))
  addFooterBox(ws, footRowNum, 1, third, 'DEVANNED BY')
  addFooterBox(ws, footRowNum, third + 1, third * 2, 'DATE')
  addFooterBox(ws, footRowNum, third * 2 + 1, totalCols, 'SIGNATURE')

  ws.views = [{ state: 'frozen', ySplit: HDR2, xSplit: 0 }]
  ws.pageSetup.printTitlesRow = `${HDR1}:${HDR2}`
  ws.pageSetup.printArea = `A1:${ws.getColumn(totalCols).letter}${ws.rowCount}`

  // Act Ctns/Act Sum/✓/Note used to live on this Plan sheet, but they're actual-
  // devanning data that doesn't exist yet when this pre-devanning sheet is
  // generated — a second tab in the same workbook, rather than dropping them.
  // Group No + Split No repeated here (not Destination/Job Ref — same
  // reconstructible-from-Group-No reasoning as the Plan sheet above) so staff
  // can still match a row back to the Plan tab while hand-writing actuals.
  addActualSheet(wb, opts)

  const buffer = await wb.xlsx.writeBuffer()
  return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
}

// ── ACTUAL ─────────────────────────────────────────────────────────────────────
// Second tab, Full workbook only: the post-devanning hand-fill columns that used
// to sit on the Plan sheet (Act Ctns / Act Sum / ✓ / Note), identified by the
// same Group No / Split No pair as the Plan tab.
function addActualSheet(wb: ExcelJS.Workbook, opts: DevanSheetExcelOptions) {
  const { jobRef, containerNo, warehouseCode, plannedDevanAt, rows } = opts
  const titleLeft = [jobRef, containerNo].filter(Boolean).join(' · ') + ' — DEVAN SHEET (ACTUAL)'
  const eta = fmtDate(plannedDevanAt)

  const ws = wb.addWorksheet('ACTUAL')
  setPageSetup(ws)
  setNativeHeader(ws, titleLeft, warehouseCode, eta)

  const COLS = [
    { id: 'splitNo',    label: 'Group No', width: 8,  wrap: true },
    { id: 'splitNos',   label: 'Split No', width: 6,  wrap: true },
    { id: 'actCartons', label: 'Act Ctns', width: 8,  align: 'center' as const },
    { id: 'actSum',     label: 'Act Sum',  width: 9.5, align: 'center' as const }, // not 9 — ExcelJS silently drops a column width of exactly 9 on write (verified)
    { id: 'check',      label: '✓',        width: 4,  align: 'center' as const },
    { id: 'note',       label: 'Note',     width: 8 },
  ]
  const totalCols = COLS.length
  ws.columns = COLS.map(c => ({ width: c.width }))

  addTitleRow(ws, titleLeft, warehouseCode, totalCols)

  const HDR = 2
  const hdrRow = ws.getRow(HDR)
  COLS.forEach((c, i) => {
    hdrRow.getCell(i + 1).value = c.label
    styleHeaderCell(hdrRow.getCell(i + 1))
  })
  hdrRow.height = 16

  rows.forEach(row => {
    const excelRow = ws.getRow(ws.rowCount + 1)
    COLS.forEach((c, i) => {
      const cell = excelRow.getCell(i + 1)
      cell.value =
        c.id === 'splitNo'  ? row.splitNo :
        c.id === 'splitNos' ? (row.splitNos ?? '') :
        '' // Act Ctns/Act Sum/✓/Note — warehouse fills in by hand once devanning happens
      cell.border = ALL_BORDERS
      cell.alignment = { vertical: 'top', wrapText: !!c.wrap, horizontal: c.align }
    })
    excelRow.height = FULL_ROW_MIN_HEIGHT
  })

  ws.views = [{ state: 'frozen', ySplit: HDR, xSplit: 0 }]
  ws.pageSetup.printTitlesRow = `${HDR}:${HDR}`
  ws.pageSetup.printArea = `A1:${ws.getColumn(totalCols).letter}${ws.rowCount}`
}

// ── SIMPLE ─────────────────────────────────────────────────────────────────────
// Mirrors BlankDevanSheetPDF's 'simple' columns exactly (8 columns) — purely
// informational, no editable/blank columns, no Notes/Remarks, no signature block.
// Row layout: 1 = title, 2 = column header, 3+ = data.
async function buildSimpleSheet(opts: DevanSheetExcelOptions): Promise<Blob> {
  const { jobRef, containerNo, warehouseCode, plannedDevanAt, rows } = opts
  const titleLeft = [jobRef, containerNo].filter(Boolean).join(' · ') + ' — DEVAN SHEET (SIMPLE)'
  const eta = fmtDate(plannedDevanAt)

  const wb = new ExcelJS.Workbook()
  const ws = wb.addWorksheet('Devan Sheet (Simple)')
  setPageSetup(ws)
  setNativeHeader(ws, titleLeft, warehouseCode, eta)

  const COLS = [
    { id: 'destination', label: 'Destination', width: 25, wrap: true },
    { id: 'splitNo',      label: 'Group No',   width: 12, wrap: true },
    { id: 'splitNos',     label: 'Split No',   width: 8,  wrap: true },
    { id: 'jobRefs',      label: 'Job Ref',    width: 25, wrap: true },
    { id: 'delMode',      label: 'Del. Mode',  width: 12 },
    { id: 'qty',          label: 'Qty',        width: 6,  numFmt: '0',    align: 'center' as const },
    { id: 'wtKg',         label: 'WT(KG)',     width: 9.5,  numFmt: '0.0' }, // not 9 — ExcelJS silently drops a column width of exactly 9 on write (verified)
    { id: 'cbm',          label: 'CBM',        width: 7,  numFmt: '0.00' },
  ]
  const totalCols = COLS.length
  ws.columns = COLS.map(c => ({ width: c.width }))

  addTitleRow(ws, titleLeft, warehouseCode, totalCols)

  const HDR = 2
  const hdrRow = ws.getRow(HDR)
  COLS.forEach((c, i) => {
    hdrRow.getCell(i + 1).value = c.label
    styleHeaderCell(hdrRow.getCell(i + 1))
  })
  hdrRow.height = 16

  rows.forEach(row => {
    const excelRow = ws.getRow(ws.rowCount + 1)
    COLS.forEach((c, i) => {
      const cell = excelRow.getCell(i + 1)
      cell.value =
        c.id === 'qty'  ? row.qty :
        c.id === 'wtKg' ? (row.wtKg ?? null) :
        c.id === 'cbm'  ? (row.cbm  ?? null) :
        (row as unknown as Record<string, unknown>)[c.id] as string ?? ''
      cell.border = ALL_BORDERS
      cell.alignment = { vertical: 'top', wrapText: !!c.wrap, horizontal: c.align }
      if ('numFmt' in c && c.numFmt) cell.numFmt = c.numFmt
    })
    const maxLines = Math.max(1,
      estimateLines(row.destination, COLS[0].width),
      estimateLines(row.jobRefs, COLS[3].width),
    )
    excelRow.height = Math.max(SIMPLE_ROW_MIN_HEIGHT, maxLines * 14 + 8)
  })

  const totalQty = rows.reduce((s, r) => s + r.qty, 0)
  const totalWt  = rows.reduce((s, r) => s + (r.wtKg ?? 0), 0)
  const totalCbm = rows.reduce((s, r) => s + (r.cbm  ?? 0), 0)
  const totalRow = ws.getRow(ws.rowCount + 1)
  for (let col = 1; col <= totalCols; col++) {
    const cell = totalRow.getCell(col)
    cell.border = ALL_BORDERS
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_FILL } }
    cell.font = { bold: true }
  }
  totalRow.getCell(1).value = 'TOTAL'
  totalRow.getCell(6).value = totalQty
  totalRow.getCell(6).numFmt = '0'
  totalRow.getCell(7).value = totalWt || ''
  totalRow.getCell(7).numFmt = '0.0'
  totalRow.getCell(8).value = totalCbm || ''
  totalRow.getCell(8).numFmt = '0.00'

  // Footnote — matches the PDF's "For information only — {containerNo}", no signature block.
  const footRow = ws.getRow(ws.rowCount + 1)
  ws.mergeCells(footRow.number, 1, footRow.number, totalCols)
  const footCell = footRow.getCell(1)
  footCell.value = `For information only — ${containerNo}`
  footCell.font = { italic: true, size: 8, color: { argb: 'FF64748B' } }
  footCell.border = { top: THIN }

  ws.views = [{ state: 'frozen', ySplit: HDR, xSplit: 0 }]
  ws.pageSetup.printTitlesRow = `${HDR}:${HDR}`
  ws.pageSetup.printArea = `A1:${ws.getColumn(totalCols).letter}${ws.rowCount}`

  const buffer = await wb.xlsx.writeBuffer()
  return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
}

export function devanSheetExcelFilename(type: 'full' | 'simple', containerNo: string, jobRef: string): string {
  const label = type === 'full' ? 'Full' : 'Simple'
  const parts = [containerNo, jobRef].filter(Boolean)
  return `Devan_Sheet_${label}_${parts.join('_')}.xlsx`
}
